import { TotemSelect } from 'components/TotemSelect';

import styled from 'styled-components';
import { MicrostoreLocationTemplateFormValues } from './FormHelper/DataMapper';
import {
    ProductArrangementProductFragmentFragment,
    ProductArrangementReassortmentType,
    ProductState,
} from 'data/__generated__';
import { PRODUCT_ARRANGEMENT_REASSORTMENT_TYPE_LABELS } from 'pages/ProductArrangements/ProductArrangementDetails/constants/types';
import { TotemInput } from 'components/TotemInput';
import { ProductSelect } from 'components/Select/ProductSelect';

const DEFAULT_PRODUCTS_FILTERS = {
    states: [ProductState.Regular],
    isMicrostore: true,
};

export const TemplateProductSelector = ({
    selectedLocationTemplate,
    updateLocation,
}: {
    selectedLocationTemplate: MicrostoreLocationTemplateFormValues;
    updateLocation: (newLocationValues: Partial<MicrostoreLocationTemplateFormValues>) => void;
}) => {
    const { productArrangement, product, stockMax, reassortmentType, trayMaxCapacity } = selectedLocationTemplate;

    const isStockReassortmentType =
        reassortmentType === ProductArrangementReassortmentType.Manual ||
        reassortmentType === ProductArrangementReassortmentType.Target;

    function onProductSelect(product: ProductArrangementProductFragmentFragment | null) {
        if (!product) {
            return;
        }

        updateLocation({
            productArrangement: null,
            product,
        });
    }

    const reassortmentTypeOptions = Object.keys(ProductArrangementReassortmentType)
        .map((type) => ({
            value: type as ProductArrangementReassortmentType,
            label: PRODUCT_ARRANGEMENT_REASSORTMENT_TYPE_LABELS[type as ProductArrangementReassortmentType],
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

    // Todo use react virtualized or react window with select. Because there are too much products
    return (
        <Container>
            <InputContainer>
                <ProductSelect
                    value={product?._id ?? null}
                    onChange={onProductSelect}
                    filters={DEFAULT_PRODUCTS_FILTERS}
                />
            </InputContainer>
            <ProductInfoContainer>
                <InputContainer>
                    <TotemSelect
                        label={`Type de réassort${productArrangement ? ' (optionnel)' : ''}`}
                        placeholder="Sélectionner un type de réassort"
                        value={reassortmentType ?? null}
                        options={reassortmentTypeOptions}
                        onChange={(value) => {
                            const newIsStockType =
                                value === ProductArrangementReassortmentType.Manual ||
                                value === ProductArrangementReassortmentType.Target;
                            updateLocation({
                                reassortmentType: value || null,
                                trayMaxCapacity:
                                    value === ProductArrangementReassortmentType.Tray ? trayMaxCapacity : 0,
                                ...(newIsStockType
                                    ? {}
                                    : {
                                          stockMax: null,
                                      }),
                            });
                        }}
                        isClearable={productArrangement ? true : false}
                    />
                </InputContainer>
                {isStockReassortmentType && (
                    <InputContainer>
                        <TotemInput
                            label={`Stock max${productArrangement ? ' (optionnel)' : ''}`}
                            placeholder="-"
                            type="number"
                            $centerText
                            step="1"
                            min="0"
                            onChange={(newStockMax) =>
                                updateLocation({
                                    stockMax: newStockMax ? parseInt(newStockMax) : null,
                                })
                            }
                            value={stockMax?.toString() ?? ''}
                            required={productArrangement ? false : true}
                        />
                    </InputContainer>
                )}
                {reassortmentType === ProductArrangementReassortmentType.Tray && (
                    <InputContainer>
                        <TotemInput
                            label="Capacité max"
                            placeholder="0"
                            type="number"
                            $centerText
                            step="1"
                            min="1"
                            onChange={(value) =>
                                updateLocation({
                                    trayMaxCapacity: value ? parseInt(value) : 0,
                                })
                            }
                            value={trayMaxCapacity?.toString() ?? '0'}
                            required
                        />
                    </InputContainer>
                )}
            </ProductInfoContainer>
        </Container>
    );
};

const Container = styled.span`
    width: 100%;
`;

const InputContainer = styled.div`
    flex-grow: 1;
`;

const ProductInfoContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-top: 15px;

    & > :not(:first-child) {
        margin-left: 15px;
    }
`;
